import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  Eye,
  PlayCircle,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SERVICES } from "../../data/services";
import API from "../../lib/api";
import { resolveUserImageUrl } from "../../lib/avatar";
import AdminFilterBar from "./AdminFilterBar";

// Small avatar that shows the customer's profile image (if any) with a
// coloured initial as a fallback. Keeps the client column visually
// consistent with the Users list where the profile image already renders.
function OrderClientAvatar({ user }) {
  const [failed, setFailed] = useState(false);
  const src = resolveUserImageUrl(user?.profile_image);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={user?.name || "profile"}
        onError={() => setFailed(true)}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-[#E8E6F8]"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-[#6C63FF]/10 text-[#6C63FF] flex items-center justify-center font-heading font-bold text-xs flex-shrink-0">
      {(user?.name || "?")[0]?.toUpperCase()}
    </div>
  );
}

const STATUS_META = {
  active:         { label: "Active",      color: "bg-blue-100 text-blue-700",     icon: PlayCircle, accent: "#3B82F6" },
  completed:      { label: "Completed",   color: "bg-green-100 text-green-700",   icon: CheckCircle, accent: "#10B981" },
  cancelled:      { label: "Cancelled",   color: "bg-red-100 text-red-600",       icon: XCircle, accent: "#E57373" },
  admin_pending:  { label: "Admin Pend.", color: "bg-orange-100 text-orange-700", icon: AlertTriangle, accent: "#F97316" },
};

const ALL_STATUSES = ["active", "completed", "cancelled", "admin_pending"];

const inrFromString = (raw) => {
  if (!raw) return null;
  const m = String(raw).match(/[\d,]+/);
  return m ? Number(m[0].replace(/,/g, "")) : null;
};

export default function OrdersTable() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ search: "", status: "", service_id: "" });
  const [data, setData] = useState({ orders: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      setFetchError("");
      try {
        const params = { page: p, limit: 20 };
        if (filters.search) params.search = filters.search;
        if (filters.status) params.status = filters.status;
        if (filters.service_id) params.service_id = filters.service_id;
        const { data: res } = await API.get("/admin/orders", { params });
        setData(res);
      } catch (err) {
        console.error(err);
        setFetchError(
          err.response?.data?.detail || err.message || "Failed to load orders"
        );
      }
      setLoading(false);
    },
    [filters, page]
  );

  useEffect(() => { load(page); }, [load, page]);

  // True per-status counts come from the backend's `counts` field — covers
  // the entire filtered dataset, not just the current page. Falls back to a
  // page-derived count if an older backend response doesn't carry it.
  const statusCounts = useMemo(() => {
    if (data.counts) return data.counts;
    const seed = { active: 0, completed: 0, cancelled: 0, admin_pending: 0 };
    for (const o of data.orders || []) {
      if (seed[o.status] !== undefined) seed[o.status] += 1;
    }
    return seed;
  }, [data.counts, data.orders]);

  const handleFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };
  const handleReset = () => {
    setFilters({ search: "", status: "", service_id: "" });
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      // Pull every matching order in one go. Backend caps `limit` at 100 per
      // request, so page through until we've collected the full set.
      const params = {};
      if (filters.search)     params.search     = filters.search;
      if (filters.status)     params.status     = filters.status;
      if (filters.service_id) params.service_id = filters.service_id;

      const all = [];
      let p = 1;
      while (true) {
        const { data: res } = await API.get("/admin/orders", {
          params: { ...params, page: p, limit: 100 },
        });
        all.push(...(res.orders || []));
        if (p >= (res.pages || 1) || (res.orders || []).length === 0) break;
        p += 1;
      }

      const headers = [
        "Order ID", "Type", "Service", "Client Name", "Client Email", "Client Phone",
        "Assigned CA", "Status", "Progress %", "Steps Completed", "Total Steps",
        "Current Step", "Pending Admin Steps", "Created",
      ];

      // Escape per RFC 4180: wrap in quotes and double any embedded quotes.
      const esc = (v) => {
        if (v == null) return "";
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = all.map((o) => {
        const completed = (o.steps || []).filter((s) => s.status === "completed").length;
        const current   = (o.steps || []).find((s) => s.status !== "completed");
        return [
          `#${o.id}`,
          o.type,
          o.service_name,
          o.user?.name,
          o.user?.email,
          o.user?.phone,
          o.ca?.name || "Unassigned",
          STATUS_META[o.status]?.label || o.status,
          o.progress_percentage,
          completed,
          o.total_steps ?? (o.steps?.length ?? ""),
          current ? `${current.step_number}. ${current.name}` : "All done",
          o.pending_admin_steps,
          o.created_at ? new Date(o.created_at).toLocaleString("en-IN") : "",
        ].map(esc).join(",");
      });

      const csv = [headers.map(esc).join(","), ...rows].join("\r\n");
      // BOM so Excel treats the file as UTF-8.
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `orders-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert(err.response?.data?.detail || "Failed to export orders");
    }
    setExporting(false);
  };

  return (
    <div className="space-y-5">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A1A2E] via-[#2A1F5C] to-[#4F46E5] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#A78BFA]/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#22D3EE]/15 rounded-full blur-3xl" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-[#A5B4FC]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Orders
              </h1>
              <p className="text-sm text-white/70 font-body mt-0.5">
                {data.total} {data.total === 1 ? "order" : "orders"} across all services
              </p>
            </div>
          </div>

          {/* Inline mini-counts for the visible page */}
          <div className="flex items-center gap-3 sm:gap-5 flex-wrap">
            {ALL_STATUSES.map((s) => {
              const meta = STATUS_META[s];
              const Icon = meta.icon;
              const active = filters.status === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleFilter("status", active ? "" : s)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-body font-medium transition-all ${
                    active
                      ? "bg-white text-[#1A1A2E] border-white shadow-sm"
                      : "bg-white/5 border-white/15 text-white/85 hover:bg-white/10"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" style={{ color: active ? meta.accent : undefined }} />
                  {meta.label}
                  <span
                    className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-heading font-bold ${
                      active ? "bg-[#1A1A2E]/10 text-[#1A1A2E]" : "bg-white/15 text-white"
                    }`}
                  >
                    {statusCounts[s]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <AdminFilterBar filters={filters} onFilterChange={handleFilter} onReset={handleReset}>
        <select
          value={filters.status}
          onChange={(e) => handleFilter("status", e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </select>
        <select
          value={filters.service_id}
          onChange={(e) => handleFilter("service_id", e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All Services</option>
          {SERVICES.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <button
          onClick={handleExport}
          disabled={exporting || data.total === 0}
          title="Download all matching orders as CSV"
          className="flex items-center gap-1.5 text-xs font-body font-medium text-white bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-60 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition-colors"
        >
          {exporting ? (
            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </AdminFilterBar>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fetchError ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-500 font-body">{fetchError}</p>
            <button onClick={() => load(page)} className="mt-2 text-xs text-[#6C63FF] underline font-body">
              Retry
            </button>
          </div>
        ) : data.orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <ClipboardList className="h-8 w-8 text-[#9CA3AF] mb-2" />
            <p className="text-sm text-[#6B7280] font-body">No orders match these filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                {/* Columns get progressively hidden on smaller screens so the
                    table stays readable without horizontal scroll. Order /
                    Client / Status / Steps / Actions are always visible. */}
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Client</th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Service</th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Assigned CA</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Status</th>
                  <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Progress</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Steps</th>
                  <th className="hidden 2xl:table-cell px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {data.orders.map((o) => {
                  const meta = STATUS_META[o.status] || STATUS_META.active;
                  const StatusIcon = meta.icon;
                  const price = inrFromString(o.price);
                  return (
                    <tr
                      key={o.id}
                      className="hover:bg-[#F5F4FB]/70 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/orders/${o.id}`)}
                    >
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <p className="font-mono text-xs text-[#1A1A2E] font-medium">#{o.id}</p>
                        <p className="text-[10px] text-[#9CA3AF] capitalize mt-0.5">{o.type}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <OrderClientAvatar user={o.user} />
                          <div className="min-w-0">
                            <p className="font-medium text-[#1A1A2E] truncate">{o.user?.name || "—"}</p>
                            <p className="text-[11px] text-[#9CA3AF] truncate">{o.user?.email}</p>
                            {/* On mobile the Service column is hidden — show it
                                here so the row still carries that context. */}
                            <p className="md:hidden text-[11px] text-[#4A4A60] truncate mt-0.5">{o.service_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3.5">
                        <p className="text-[#4A4A60] max-w-[180px] truncate">{o.service_name}</p>
                        {price && <p className="text-[11px] text-[#10B981] font-medium mt-0.5">₹{price.toLocaleString("en-IN")}</p>}
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3.5 whitespace-nowrap">
                        {o.ca?.name ? (
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#EEEDFC] flex items-center justify-center text-[10px] font-bold font-heading text-[#6C63FF] flex-shrink-0">
                              {o.ca.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs font-body text-[#1A1A2E]">{o.ca.name}</span>
                          </div>
                        ) : (
                          <span className="text-[11px] font-body italic text-[#9CA3AF]">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${meta.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {meta.label}
                        </span>
                        {o.pending_admin_steps > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold">
                            {o.pending_admin_steps}
                          </span>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3.5 min-w-[160px]">
                        <div className="flex items-center gap-2.5">
                          <div className="flex-1 h-1.5 bg-[#E8E6F8] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${o.progress_percentage}%`,
                                background:
                                  o.status === "completed"
                                    ? "#10B981"
                                    : o.status === "cancelled"
                                      ? "#E57373"
                                      : "linear-gradient(90deg,#6C63FF,#A78BFA)",
                              }}
                            />
                          </div>
                          <span className="text-xs text-[#6B7280] font-body min-w-[34px] text-right">
                            {o.progress_percentage}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 align-middle">
                        {o.steps && o.steps.length > 0 ? (() => {
                          const completed = o.steps.filter((s) => s.status === "completed").length;
                          // "Current" = first step that isn't completed, otherwise last step
                          const current =
                            o.steps.find((s) => s.status !== "completed") ||
                            o.steps[o.steps.length - 1];
                          const allDone = completed === o.steps.length;
                          return (
                            <div className="min-w-[180px] max-w-[240px]">
                              <div className="flex items-center gap-1 mb-1.5">
                                {o.steps.map((s) => {
                                  const done = s.status === "completed";
                                  const isCurrent = s.step_number === current.step_number && !allDone;
                                  return (
                                    <span
                                      key={s.step_number}
                                      title={`${s.step_number}. ${s.name} — ${done ? "Completed" : s.status.replace("_", " ")}`}
                                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-heading font-bold flex-shrink-0 border ${
                                        done
                                          ? "bg-[#10B981] text-white border-[#10B981]"
                                          : isCurrent
                                            ? "bg-white text-[#E57373] border-[#E57373] ring-2 ring-[#E57373]/20"
                                            : "bg-red-50 text-[#E57373] border-[#FCA5A5]"
                                      }`}
                                    >
                                      {done ? "✓" : s.step_number}
                                    </span>
                                  );
                                })}
                              </div>
                              <div className="flex items-baseline gap-1.5">
                                <span className={`text-[10px] font-body font-semibold uppercase tracking-wide ${allDone ? "text-[#10B981]" : "text-[#E57373]"}`}>
                                  {allDone ? "All done" : `Step ${current.step_number}`}
                                </span>
                                <span className="text-[11px] text-[#4A4A60] font-body truncate" title={current.name}>
                                  · {current.name}
                                </span>
                              </div>
                              <p className="text-[10px] text-[#9CA3AF] font-body mt-0.5">
                                {completed} / {o.steps.length} completed
                              </p>
                            </div>
                          );
                        })() : (
                          <span className="text-[11px] text-[#9CA3AF]">–</span>
                        )}
                      </td>
                      <td className="hidden 2xl:table-cell px-4 py-3.5 text-xs text-[#9CA3AF] whitespace-nowrap">
                        {new Date(o.created_at).toLocaleDateString("en-IN")}
                      </td>
                      <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 justify-end">
                          {o.user?.phone && (
                            <a
                              href={`https://wa.me/${String(o.user.phone).replace(/\D/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`WhatsApp ${o.user.name || "client"}`}
                              className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white transition-colors"
                            >
                              <WhatsAppIcon className="h-3.5 w-3.5" />
                            </a>
                          )}
                          <button
                            onClick={() => navigate(`/admin/orders/${o.id}`)}
                            className="inline-flex items-center gap-1 text-xs text-[#6C63FF] font-medium px-2.5 py-1 rounded-lg hover:bg-[#6C63FF]/10 transition-colors"
                          >
                            <Eye className="h-3.5 w-3.5" /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, data.total)} of {data.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {page} / {data.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
    </svg>
  );
}
